import { useUsersAccessTechnicianEditor } from '../../hooks/useUsersAccessTechnicianEditor'
import type { UsersAccessTechnicianRowI } from '../../types/usersAccess'
import { useTranslation } from 'react-i18next'

import { Alert, Modal, Skeleton, Stack } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

import { UsersAccessTechnicianForm } from './UsersAccessTechnicianForm'

interface EditUsersAccessTechnicianModalIProps {
  colorScheme: ColorScheme
  isOpen: boolean
  onClose: () => void
  row: UsersAccessTechnicianRowI
}

export const EditUsersAccessTechnicianModal = ({
  colorScheme,
  isOpen,
  onClose,
  row,
}: EditUsersAccessTechnicianModalIProps) => {
  const { t } = useTranslation()
  const {
    canEdit,
    errors,
    facilityOptions,
    isError,
    isLoading,
    isSaving,
    selectedFacilityOptions,
    setFacilitySearch,
    setField,
    submit,
    values,
  } = useUsersAccessTechnicianEditor(row.id, colorScheme)
  const save = async () => {
    const didSave = await submit()
    if (didSave) onClose()
  }

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      closeOnEsc={!isSaving}
      closeOnOverlayClick={!isSaving}
      colorScheme={colorScheme}
      isCloseHidden={isSaving}
      isOpen={isOpen}
      isSaveDisabled={isLoading || isError || !canEdit}
      isSaveLoading={isSaving}
      maxW='600px'
      onClose={onClose}
      onSave={save}
      saveButtonLabel={t('settings:rebrand_users_edit_submit')}
      showCancelButton={!isSaving}
      size='compact'
      title={t('settings:rebrand_users_edit_technician_title')}
    >
      {isLoading ? (
        <Stack gap='16px'>
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton h='52px' key={index} w='full' />
          ))}
        </Stack>
      ) : isError ? (
        <Alert
          colorScheme={colorScheme}
          description={t('settings:rebrand_users_editor_load_error')}
          variant='error'
        />
      ) : (
        <UsersAccessTechnicianForm
          colorScheme={colorScheme}
          errors={errors}
          facilityOptions={facilityOptions}
          onFacilitySearchChange={setFacilitySearch}
          selectedFacilityOptions={selectedFacilityOptions}
          setField={setField}
          testIdPrefix='EditUsersAccessTechnicianModal'
          values={values}
        />
      )}
    </Modal>
  )
}
